import { FieldDescriptor } from "./fieldTypes";

/**
 * The Catalog-document properties panel's field registry.
 *
 * **Deviation from the plan's own "derive keys from `keyof Catalog`" default:**
 * `@equipped/core/core/catalog.ts`'s hand-written `Catalog` TS interface is
 * MISSING `castable`/`tvMedia` — a real, verified drift from the actual runtime
 * `CatalogSchema` (Zod), which DOES parse/accept both (docs/251-MEDIA-CASTING.md /
 * docs/322-ROKU-FEED-COVERAGE.md v0 Part 3's own schema-only note). The same gap
 * exists on `CatalogPackEntry`/`CatalogNodeEntry` (see `catalogPackEntryFields.ts`/
 * `catalogNodeEntryFields.ts`'s own copy of this note) — a pre-existing bug in
 * `@equipped/core`, reported alongside this doc's own PR, NOT fixed here (out of
 * scope for a Mantis-admin-editor task; see CLAUDE.md's investigate-before-fixing
 * rule). Rather than silently under-cover the schema by trusting `keyof Catalog`,
 * this registry is checked against a LOCAL, hand-verified key list (`CatalogFieldKey`
 * below, read straight off `CatalogSchema`'s own Zod object shape) so the editor's
 * own coverage claim stays honest regardless of the upstream interface gap.
 *
 * Excludes `entries` (tree-managed) and `specVersion` (fixed at `1`).
 */
type CatalogFieldKey =
  | "id"
  | "title"
  | "languages"
  | "facets"
  | "countLabels"
  | "description"
  | "visibility"
  | "platforms"
  | "castable"
  | "tvMedia"
  | "showSourceUrl"
  | "retracted"
  | "retractedReason"
  | "terms";

export const catalogFields = {
  id: { section: "Identity", label: "Catalog id", kind: "text" },
  title: { section: "Identity", label: "Title", kind: "text" },
  description: { section: "Identity", label: "Description", kind: "textarea" },
  languages: { section: "Discovery", label: "Languages represented", kind: "stringArray" },
  facets: { section: "Discovery", label: "Filter facets", kind: "facets" },
  countLabels: { section: "Discovery", label: "Count-label nouns", kind: "countLabels" },
  showSourceUrl: {
    section: "Discovery",
    label: "Show hosting URL in Manage Catalogs",
    kind: "boolean3",
  },

  visibility: {
    section: "Access & capabilities",
    label: "Visibility",
    kind: "select",
    options: [
      { value: "public", label: "public" },
      { value: "private", label: "private" },
      { value: "hidden", label: "hidden" },
    ],
  },
  platforms: { section: "Access & capabilities", label: "Platform availability (opt-out)", kind: "recordBoolean" },
  castable: { section: "Access & capabilities", label: "Castable (AirPlay/Chromecast)", kind: "boolean3" },
  tvMedia: {
    section: "Access & capabilities",
    label: "Has TV media (skip-hint)",
    kind: "boolean3",
    help: "false = a hint this whole catalog subtree is media-less, so the Roku feed walk skips it.",
  },
  terms: { section: "Access & capabilities", label: "Terms of usage", kind: "catalogTerms" },

  retracted: {
    section: "Lifecycle",
    label: "Retracted (tombstone)",
    kind: "boolean3",
    help: "true = this catalog is retired — a user who auto-remove-on-retraction is opted in stops seeing it.",
  },
  retractedReason: { section: "Lifecycle", label: "Retracted reason", kind: "text" },
} satisfies Record<CatalogFieldKey, FieldDescriptor>;
